package br.com.vvrefeicoes.portal.feature.social

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.decodeToImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.decodeBase64
import br.com.vvrefeicoes.portal.domain.model.SocialConnection
import br.com.vvrefeicoes.portal.domain.model.SocialConnectionType
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun SocialConnectionsScreen() {
    val repository = LocalAppGraph.current.socialConnectionRepository
    val scope = rememberCoroutineScope()
    var connections by remember { mutableStateOf<List<SocialConnection>>(emptyList()) }
    var error by remember { mutableStateOf<String?>(null) }
    var showCreate by remember { mutableStateOf(false) }
    var pairingConnection by remember { mutableStateOf<SocialConnection?>(null) }

    fun load() {
        scope.launch {
            runCatching { connections = repository.list() }
                .onFailure { error = it.message ?: "Não foi possível carregar as conexões." }
        }
    }

    LaunchedEffect(Unit) { load() }

    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        footer = { AppFooter() },
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
    ) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Column {
                Text("Ferramentas", style = MaterialTheme.typography.headlineMedium)
                Text("Redes sociais", style = MaterialTheme.typography.titleLarge)
            }
            AppButton("Nova conexão", { showCreate = true })
        }
        Text(
            "Cadastre conexões que poderão ser utilizadas futuramente para publicações e mensagens.",
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        error?.let { Text(it, color = MaterialTheme.colorScheme.error) }
        if (connections.isEmpty()) {
            Text("Nenhuma conexão cadastrada.")
        } else {
            connections.forEach { connection ->
                ConnectionCard(
                    connection = connection,
                    onConnect = {
                        scope.launch {
                            runCatching { repository.connect(connection.id) }
                                .onSuccess { updated ->
                                    connections = connections.map { if (it.id == updated.id) updated else it }
                                    pairingConnection = updated
                                }.onFailure { error = it.message ?: "Não foi possível iniciar a conexão." }
                        }
                    },
                    onDelete = {
                        scope.launch {
                            runCatching { repository.delete(connection.id) }
                                .onSuccess { connections = connections.filterNot { it.id == connection.id } }
                                .onFailure { error = it.message ?: "Não foi possível excluir a conexão." }
                        }
                    },
                )
            }
        }
    }

    pairingConnection?.let { connection ->
        LaunchedEffect(connection.id) {
            var current = connection
            while (current.status in setOf("CONNECTING", "WAITING_QR")) {
                runCatching { repository.get(connection.id) }
                    .onSuccess { updated ->
                        current = updated
                        connections = connections.map { if (it.id == updated.id) updated else it }
                        pairingConnection = updated
                    }
                if (current.status in setOf("CONNECTING", "WAITING_QR")) delay(1500)
            }
        }
        PairingDialog(connection, onDismiss = { pairingConnection = null })
    }

    if (showCreate) {
        CreateConnectionDialog(
            onDismiss = { showCreate = false },
            onCreated = { connection ->
                connections = connections + connection
                showCreate = false
            },
            onError = { error = it },
        )
    }
}

@Composable
private fun ConnectionCard(
    connection: SocialConnection,
    onConnect: () -> Unit,
    onDelete: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        tonalElevation = 2.dp,
        shape = MaterialTheme.shapes.medium,
    ) {
        Column(Modifier.padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Column(Modifier.weight(1f)) {
                    Text(connection.name, style = MaterialTheme.typography.titleMedium)
                    Text("${connection.type.label} · ${statusLabel(connection.status)}")
                    if (connection.type != SocialConnectionType.BAILEYS) {
                        Text(
                            if (connection.credentialsConfigured) "Credenciais configuradas" else "Credenciais pendentes",
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    connection.phoneNumber?.let { Text(it, color = MaterialTheme.colorScheme.onSurfaceVariant) }
                }
                if (connection.type == SocialConnectionType.BAILEYS) {
                    AppButton(
                        text =
                            if (connection.status == "CONNECTING" || connection.status == "WAITING_QR") {
                                "Abrir pareamento"
                            } else {
                                "Conectar"
                            },
                        onClick = onConnect,
                        enabled = connection.status != "CONNECTED",
                        variant = PortalButtonVariant.Secondary,
                    )
                }
                TextButton(onClick = onDelete) { Text("Excluir") }
            }
            if (!connection.qrCode.isNullOrBlank()) {
                Text("QR Code disponível. Clique em \"Abrir pareamento\" para visualizar.", style = MaterialTheme.typography.bodySmall)
            }
        }
    }
}

@Composable
private fun CreateConnectionDialog(
    onDismiss: () -> Unit,
    onCreated: (SocialConnection) -> Unit,
    onError: (String) -> Unit,
) {
    val repository = LocalAppGraph.current.socialConnectionRepository
    val scope = rememberCoroutineScope()
    var name by remember { mutableStateOf("") }
    var type by remember { mutableStateOf(SocialConnectionType.BAILEYS) }
    var token by remember { mutableStateOf("") }
    var phoneNumberId by remember { mutableStateOf("") }
    var businessAccountId by remember { mutableStateOf("") }
    var accountId by remember { mutableStateOf("") }
    var pageId by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    val canSave = name.isNotBlank() && (type == SocialConnectionType.BAILEYS || token.isNotBlank()) && !saving
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Nova conexão social") },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                AppTextField(name, { name = it }, label = "Nome da conexão", modifier = Modifier.fillMaxWidth())
                SearchableSelect(
                    leadingIcon = null,
                    options = SocialConnectionType.entries.map { it.label },
                    selected = type.label,
                    onSelect = { selected ->
                        type = SocialConnectionType.entries.first { it.label == selected }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    placeholder = "Selecione o tipo",
                    underlined = true,
                )
                if (type != SocialConnectionType.BAILEYS) {
                    AppTextField(token, { token = it }, label = "Token de acesso", modifier = Modifier.fillMaxWidth())
                }
                when (type) {
                    SocialConnectionType.WHATSAPP_OFFICIAL -> {
                        AppTextField(phoneNumberId, { phoneNumberId = it }, label = "ID do telefone", modifier = Modifier.fillMaxWidth())
                        AppTextField(businessAccountId, { businessAccountId = it }, label = "ID da conta WhatsApp Business", modifier = Modifier.fillMaxWidth())
                    }
                    SocialConnectionType.INSTAGRAM ->
                        AppTextField(accountId, { accountId = it }, label = "ID da conta profissional", modifier = Modifier.fillMaxWidth())
                    SocialConnectionType.FACEBOOK ->
                        AppTextField(pageId, { pageId = it }, label = "ID da página", modifier = Modifier.fillMaxWidth())
                    SocialConnectionType.BAILEYS -> Unit
                }
            }
        },
        confirmButton = {
            TextButton(
                enabled = canSave,
                onClick = {
                    saving = true
                    scope.launch {
                        runCatching { repository.create(name.trim(), type, credentialsFor(type, token, phoneNumberId, businessAccountId, accountId, pageId)) }
                            .onSuccess(onCreated)
                            .onFailure { onError(it.message ?: "Não foi possível criar a conexão.") }
                        saving = false
                    }
                },
            ) { Text("Salvar") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancelar") } },
    )
}

private fun credentialsFor(
    type: SocialConnectionType,
    token: String,
    phoneNumberId: String,
    businessAccountId: String,
    accountId: String,
    pageId: String,
): Map<String, String> =
    buildMap {
        if (type != SocialConnectionType.BAILEYS) put("accessToken", token)
        when (type) {
            SocialConnectionType.WHATSAPP_OFFICIAL -> {
                put("phoneNumberId", phoneNumberId)
                put("businessAccountId", businessAccountId)
            }
            SocialConnectionType.INSTAGRAM -> put("accountId", accountId)
            SocialConnectionType.FACEBOOK -> put("pageId", pageId)
            SocialConnectionType.BAILEYS -> Unit
        }
    }

@Composable
private fun PairingDialog(
    connection: SocialConnection,
    onDismiss: () -> Unit,
) {
    var qrBitmap by remember(connection.qrCode) { mutableStateOf<ImageBitmap?>(null) }
    LaunchedEffect(connection.qrCode) {
        val encoded = connection.qrCode?.substringAfter("base64,", "")
        qrBitmap =
            encoded?.takeIf { it.isNotBlank() }?.let { value ->
                runCatching { decodeBase64(value).decodeToImageBitmap() }.getOrNull()
            }
    }
    AppModalCard(title = "Conectar ${connection.name}", onDismiss = onDismiss) {
        Text("Status: ${statusLabel(connection.status)}")
        if (connection.qrCode.isNullOrBlank()) {
            Text("Aguardando o QR Code do provedor Baileys...", color = MaterialTheme.colorScheme.onSurfaceVariant)
        } else {
            Text("Leia este QR Code no WhatsApp para concluir o pareamento:")
            qrBitmap?.let { bitmap ->
                Surface(color = MaterialTheme.colorScheme.surfaceVariant, shape = MaterialTheme.shapes.medium) {
                    Image(bitmap, contentDescription = "QR Code do WhatsApp", contentScale = ContentScale.Fit, modifier = Modifier.size(320.dp).padding(AppSpacing.space16))
                }
            }
        }
        TextButton(onClick = onDismiss) { Text("Fechar") }
    }
}

private fun statusLabel(status: String): String =
    when (status) {
        "DISCONNECTED" -> "Desconectada"
        "CONNECTING" -> "Conectando"
        "WAITING_QR" -> "Aguardando QR Code"
        "CONNECTED" -> "Conectada"
        "ERROR" -> "Erro"
        else -> status
    }
